import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

/**
 * RnD-D — Evidence Pack panel.
 *
 * Read-only compliance surface for the R&D-defensibility evidence pack.
 * Lists the most recent packs (last 10) with download buttons and a
 * "Generate new pack" action. Auth is enforced server-side (director /
 * admin / MLRO); non-eligible users simply see an error state.
 */
export default function EvidencePackPanel({ authHeaders }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const headers = useCallback(() => {
    if (typeof authHeaders === "function") return authHeaders();
    const email =
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      "";
    return { "X-Bonsai-User-Id": email };
  }, [authHeaders]);

  const fetchPacks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/compliance/evidence-pack/list`, {
        headers: headers(),
      });
      const rows = Array.isArray(res.data?.packs) ? res.data.packs : [];
      setPacks(rows.slice(0, 10));
    } catch (err) {
      setError(errorMessage(err, "request failed"));
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      await axios.post(
        `${API}/compliance/evidence-pack/generate`,
        {},
        { headers: headers() }
      );
      await fetchPacks();
    } catch (err) {
      setError(errorMessage(err, "generation failed"));
    } finally {
      setGenerating(false);
    }
  }, [headers, fetchPacks]);

  const handleDownload = useCallback(
    async (packId) => {
      setDownloadingId(packId);
      try {
        const res = await axios.get(
          `${API}/compliance/evidence-pack/${packId}/download`,
          { headers: headers(), responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${packId}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        setError(errorMessage(err, "download failed"));
      } finally {
        setDownloadingId(null);
      }
    },
    [headers]
  );

  return (
    <section
      data-testid="evidence-pack-panel"
      className="rounded-xl border border-white/[0.07] bg-navy-card p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-100">Evidence Pack</h2>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          data-testid="evidence-pack-generate"
          className={
            "px-2.5 py-1 rounded text-xs font-medium border " +
            (generating
              ? "bg-slate-800 text-slate-400 border-slate-700/50 cursor-not-allowed"
              : "bg-slate-700 text-slate-100 border-slate-600 hover:bg-slate-600")
          }
        >
          {generating ? "Generating…" : "Generate new pack"}
        </button>
      </div>

      <p className="text-xs text-slate-400">
        R&amp;D-defensibility bundle consolidating audit, silent-fallback, and
        AI data-flow evidence. Read-only.
      </p>

      {error ? (
        <div className="rounded-md border border-red-800/50 bg-red-950/30 p-3 text-sm text-red-200">
          <span data-testid="evidence-pack-error">{error}</span>{" "}
          <button
            type="button"
            onClick={fetchPacks}
            className="ml-2 px-2 py-0.5 rounded border border-red-700/60 hover:bg-red-900/30"
          >
            Retry
          </button>
        </div>
      ) : loading && packs.length === 0 ? (
        <div
          data-testid="evidence-pack-skeleton"
          className="h-4 w-44 bg-slate-800 rounded animate-pulse"
        />
      ) : packs.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="evidence-pack-empty">
          No evidence packs generated yet.
        </div>
      ) : (
        <table
          className="w-full text-sm text-slate-200"
          data-testid="evidence-pack-table"
        >
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-400">
              <th className="text-left py-1 pr-3">Pack ID</th>
              <th className="text-left py-1 pr-3">Generated</th>
              <th className="text-left py-1 pr-3">By</th>
              <th className="text-right py-1 pr-3">Size</th>
              <th className="text-right py-1">Download</th>
            </tr>
          </thead>
          <tbody>
            {packs.map((p) => (
              <tr
                key={p.pack_id}
                data-testid="evidence-pack-row"
                className="border-t border-slate-800/60"
              >
                <td className="py-1 pr-3 font-mono text-xs">{p.pack_id}</td>
                <td className="py-1 pr-3" title={p.generated_at}>
                  {formatRelative(p.generated_at)}
                </td>
                <td className="py-1 pr-3">{p.generated_by || "—"}</td>
                <td className="py-1 pr-3 text-right font-mono text-xs">
                  {formatBytes(p.size_bytes)}
                </td>
                <td className="py-1 text-right">
                  <button
                    type="button"
                    onClick={() => handleDownload(p.pack_id)}
                    disabled={downloadingId === p.pack_id}
                    data-testid="evidence-pack-download"
                    className="px-2 py-0.5 rounded text-xs border border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700 disabled:opacity-50"
                  >
                    {downloadingId === p.pack_id ? "…" : "Download"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function formatBytes(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const b = Number(n);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}
